/**
 * Responsible for creating an accordion based on elements existing in the DOM
 * @module accordion
 */

import $ from 'jquery';

import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'accordion';
export const CLICK_EVENT = 'click';
export const AUTO_VALUE = 'auto';
export const BLOCK_VALUE = 'block';
export const CHECKED_ATTR = 'checked';

export const DEFAULTS = {
  accItem: '.accordion__item',
  accContent: '.accordion__content',
  accActive: 'accordion__item--active',
  accRadio: 'js-accordion-radio',
  accLabel: '.accordion__label',
  accSpeed: 500,
  accToggle: false,
  accDataToggleAttr: 'toggle',
  accTargetItem: '',
  accQueryOpen: false,
  accDataQueryAttr: 'queryString',
  onComplete: () => {}
};

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, accordion);

/**
 * Initializes an accordion based on provided parameters.
 * @param {HTMLElement} el - An element containing the elements for the
 * accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
export default function accordion(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions($el, options)
  addAccContentAria($el);
  applyDefaultsToActive($el, opts);
  applyDefaultsToRadio($el, opts);
  applyEventHandlers($el, opts);
  return {
    openPanel: (targetItem) => {
      opts.accTargetItem = targetItem;
      itemClickHandler('', $el, opts);
    },
  };
};

/**
 * Merges together all the options and the various conditions in which they
 * can be set.
 * @param  {jQuery} $el - The container element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options
 */
const mergeOptions = ($el, options = {}) => {
  let opts = $.extend({}, DEFAULTS, options);
  const { accDataToggleAttr, accDataQueryAttr } = opts;
  if ($el.data(accDataToggleAttr) === true) {
    opts = $.extend({}, opts, { accToggle: true })
  }

  if ($el.data(accDataQueryAttr) === true) {
    opts = $.extend({}, opts, { accQueryOpen: true });
  }

  return opts;
};

/**
 * Handles any sort of defaults when the accordion is called upon.
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 */
const applyDefaultsToActive = ($el, { accActive, accContent, accLabel }) => {
  $(`.${accActive}`, $el)
    .find(accContent)
    .css({
      display: BLOCK_VALUE,
      height: AUTO_VALUE
    })
    .end()
    .find(accLabel).attr('aria-expanded', "true");
};

/**
 * Handles any sort of defaults when the accordion is using radio buttons.
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 */
const applyDefaultsToRadio = ($el, { accActive, accContent, accLabel, accItem, accRadio }) => {
  $(`.${accRadio}:${CHECKED_ATTR}`, $el)
    .closest(accItem)
    .addClass(accActive)
    .children(accContent)
    .css({
      display: BLOCK_VALUE,
      height: AUTO_VALUE
    })
    .end()
    .closest(accItem).children(accLabel).attr('aria-expanded', "true");
};

/**
 * Utility function to pull query parameter form URL
 * @private
 * @param {String} name - String to look for in URL
 */
const getParameterByName = (name) => {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  var regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),
      results = regex.exec(location.search);
  return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
}

/**
 * Applies any sort of event handlers for the accordion.
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
const applyEventHandlers = ($el, opts = {}) => {
  const { accLabel, accItem, accQueryOpen } = opts;
  // check if accordion has data attr set to true
  if (accQueryOpen) {
    // check for query string in URL
    var accordionOpen = getParameterByName('accordionOpen');
  	if (accordionOpen) {
      // set parent for openTab
      const $parent = $el.find('#' + accordionOpen).closest(accItem);

      //  Set animation speed to 0 so default active tab closes as quick as possible.
      closeActiveTab($el, opts);
      openTab($el, $parent, opts);
    }
  }
  $el
    .find(accLabel)
    .on(CLICK_EVENT, evt => itemClickHandler(evt, $el, opts));
};

/**
 * Handles the click event when a label has been iteracted with.
 * @param {jQuery.Event} evt - A jQuery event
 * @param {HTMLElement} currentTarget - The current target interacted with
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} opts - Options passed along from initialization
 */
 const itemClickHandler = (evt, $el, opts) => {
   const { accActive, accItem, accToggle } = opts;
   let $targetItem = '';
   if(evt !== ''){
     if($(evt.target).is('[type="radio"]') || $(evt.target).is('[type="checkbox"]')) {
       return;
     }
     $targetItem = $(evt.currentTarget).closest(accItem);
   } else {
     $targetItem = $(opts.accTargetItem);
   }
   if (!$targetItem.hasClass(accActive)) {
     if (!accToggle) {
       closeActiveTab($targetItem, opts);
     }
     openTab($el, $targetItem, opts);
   } else {
     closeExistingActiveTab($targetItem, opts);
   }
 };

/**
 * Opens a tab and provides any DOM manipulation and styles used to show a tab.
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {jQuery} $targetItem - Target Accordion Clicked Item
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 * @param  {Function} options.onComplete - Called on when animation has
 * completed
 */
 const openTab = ($el, $targetItem, { accActive, accContent, accLabel, accRadio, accSpeed, onComplete }) => {
  const content = $targetItem.children(accContent);
  if($targetItem.data('truncateHeight') === true){
    $targetItem.css({ height: 50, overflow: 'hidden' }).stop().animate(
      { height: 340 },
      accSpeed, function() {
        content.css({ paddingBottom: '66px' })
      });
  }
  content.css({ height: AUTO_VALUE, display: BLOCK_VALUE, overflowY: 'auto' });
  const label = $targetItem.children(accLabel);
  label.attr('aria-expanded', 'true');
  const contentHeight = content.outerHeight(true);

  content.css({ height: 0 }).stop().animate(
    { height: contentHeight },
    accSpeed,
    function() {
      content.css({
        height: '100%' //AUTO_VALUE - 100% doesn't seem to cause any issues
      })
      onComplete();
    }
  );
  $targetItem
  .addClass(accActive)
  .children(accRadio).prop(CHECKED_ATTR, true);
};

/**
 * Closes the active tab
 * @param  {jQuery} $el - Target Accordion Clicked Item
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 */

const closeActiveTab = ($el, { accActive, accContent, accLabel, accItem, accSpeed }) => {
  $el.closest('.accordion').children(`.${accActive}`).find(accContent).stop().animate(
    { height: 0 },
    accSpeed,
    function() {
      $(this).removeAttr('style');
    }
  );
  $el.closest('.accordion').children(`.${accActive}`).removeClass(accActive)
  .children(`${accLabel}`).attr('aria-expanded', "false");
  if($el.data('truncateHeight') === true){
    $el.stop().animate(
      { height: 50 }, // height of accordion item element
      accSpeed,
      function(){
        $(this).css({ height: AUTO_VALUE });
      });
  }
};

/**
 * Closes an existing tab that has directly been interacted with.
 * @param  {jQuery} $parent - The parent containing the entire tab.
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 */
const closeExistingActiveTab = ($parent, { accActive, accContent, accLabel, accSpeed }) => {
  $parent.find(accContent).stop().animate(
    { height: 0 },
    accSpeed,
    function() {
      $(this).removeAttr('style');
      $parent.removeClass(accActive).children(accLabel).attr('aria-expanded', "false");

    }
  );
  if($parent.data('truncateHeight') === true){
    $parent.stop().animate(
      { height: 50 }, // height of accordion item element
      accSpeed,
      function(){
        $(this).css({ height: AUTO_VALUE });
      });
  }
};

/**
 * Add aria-labelledby to modal content for accessible
 * audible title that corresponds with modal's heading
 * @param  {jQuery} $el
 */
 const addAccContentAria = ($el) => {
  let $accLabel = $($el.find('.accordion__label'));
  if ($accLabel.length && !$accLabel.attr('id')) {
    const accContentTargetId = `accordion__content-${Math.ceil(Math.random() * 10000000000)}`;
    $accLabel.attr('id', accContentTargetId).attr('tabindex', '0');
  }
};



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/accordion.js